import React, { useState, useEffect } from 'react';
import { ReviewForm } from './ReviewForm';
import { ReviewDisplay } from './ReviewDisplay';
import { ReviewTargetType, RatingStats } from '@/types/reviews';
import { ReviewService } from '@/lib/reviewService';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Star, Plus } from 'lucide-react';

interface ReviewSectionProps {
  targetType: ReviewTargetType;
  targetId: string;
  targetName?: string;
  className?: string;
}

export const ReviewSection: React.FC<ReviewSectionProps> = ({
  targetType,
  targetId,
  targetName,
  className = ''
}) => {
  const { user, isLoading } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const handleReviewSuccess = () => {
    // Refresh the review display
    setRefreshKey(prev => prev + 1);
    // Hide the review form
    setShowReviewForm(false);
    // Reload stats
    loadStats();
  };

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const statsData = await ReviewService.getRatingStats(targetType, targetId);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [targetType, targetId, refreshKey]);

  const WriteReviewSection = () => {
    if (isLoading) {
      return (
        <Card className="mb-6">
          <CardContent className="pt-6 text-center">
            <Star className="w-12 h-12 mx-auto mb-4 text-gray-300 animate-pulse" />
            <h3 className="font-medium mb-2">Loading...</h3>
            <p className="text-gray-600 mb-4">
              Checking authentication status
            </p>
          </CardContent>
        </Card>
      );
    }

    if (!user) {
      return (
        <Card className="mb-6">
          <CardContent className="pt-6 text-center">
            <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="font-medium mb-2">Share Your Experience</h3>
            <p className="text-gray-600 mb-4">
              Sign in to write a review and help others discover great {targetType}s
            </p>
            <Button variant="outline">
              Sign In to Review
            </Button>
          </CardContent>
        </Card>
      );
    }

    if (showReviewForm) {
      return (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <ReviewForm
              targetType={targetType}
              targetId={targetId}
              targetName={targetName}
              onSuccess={handleReviewSuccess}
              onCancel={() => setShowReviewForm(false)}
            />
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium mb-1">Have you tried this {targetType}?</h3>
              <p className="text-sm text-gray-600">
                Share your experience to help others make informed decisions
              </p>
            </div>
            <Button onClick={() => setShowReviewForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Write Review
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };


  const hasReviews = stats && stats.rating_count > 0;

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reviews & Ratings</h2>
      </div>

      {/* Write Review Section */}
      <WriteReviewSection />

      {/* Reviews Display - Only shown when reviews exist */}
      {!loadingStats && hasReviews && (
        <ReviewDisplay
          key={refreshKey}
          targetType={targetType}
          targetId={targetId}
        />
      )}

      {/* Loading state for stats */}
      {loadingStats && (
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      )}
    </div>
  );
};

export default ReviewSection; 